import {
  getRememberedScheme,
  type SchemeMemoryKey,
} from '@p2p/pages/Redirect/deeplink/schemeMemory';

/**
 * P2P-3116: кнопка «Открыть Альфа-Банк» на странице alfa_page открывает сканер QR в приложении.
 *
 * Android - одна схема `alfabank://qr_payment`. На iOS официального приложения в App Store нет, есть
 * клоны под другими схемами (список из ТЗ). Узнать со страницы, какой из них стоит, iOS не даёт, а
 * каждая схема, которой на телефоне нет, - это ошибка Safari «адрес недействителен», которую скрипт
 * закрыть не может. Её закрывает только новая загрузка страницы, поэтому перебор идёт на отдельной
 * лёгкой странице `public/alfa-open.html` (одна загрузка на схему; без бандла, того же цвета, что и
 * страница оплаты). Схема, на которой приложение открылось, запоминается (общий ключ с переборами
 * P2P-3065), и со следующего раза открывается сразу с этой страницы, без перебора.
 *
 * Список iOS-схем продублирован в alfa-open.html (статическая страница не видит бандл); их
 * совпадение проверяет тест.
 */
export const ALFA_QR_PATH = 'qr_payment';

export const ALFA_QR_ANDROID_SCHEME = 'alfabank';

export const ALFA_QR_IOS_SCHEMES = [
  'aholder',
  'kluch',
  'advance',
  'a-interior',
  'alfoto',
  'splitactivities',
  'assistmekz',
  'cashline',
  'triptally',
  'kittycash',
  'aconcierge',
];

// Официальное приложение, установленное до удаления из App Store: если оно уже открывалось у
// плательщика (запомнил перебор P2P-3065), пробуем его, хотя в списке для перебора его нет.
const IOS_REMEMBERED_EXTRA = ['alfabank'];

export const ALFA_OPEN_PAGE = '/alfa-open.html';

/** Параметр, с которым alfa-open.html возвращает плательщика на страницу оплаты. */
export const ALFA_OPEN_RESULT_PARAM = 'alfa_open';

export const ALFA_MEMORY_KEY: SchemeMemoryKey = { bank: 'alfa', platform: 'ios' };

export type AlfaPlatform = 'ios' | 'android';

/** iPhone/iPad по User-Agent; iPad в режиме «как на компьютере» выдаёт себя за Mac с тачскрином. */
export const detectAlfaPlatform = (
  userAgent: string = navigator.userAgent,
  platform: string = navigator.platform,
  maxTouchPoints: number = navigator.maxTouchPoints,
): AlfaPlatform | null => {
  if (/iphone|ipad|ipod/i.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)) {
    return 'ios';
  }

  if (/android/i.test(userAgent)) {
    return 'android';
  }

  return null;
};

export const alfaQrLink = (scheme: string): string => `${scheme}://${ALFA_QR_PATH}`;

/** Запомненная iOS-схема, если это одно из приложений Альфы. */
export const getRememberedAlfaIosScheme = (): string | null => {
  const scheme = getRememberedScheme(ALFA_MEMORY_KEY);

  if (!scheme) {
    return null;
  }

  return [...ALFA_QR_IOS_SCHEMES, ...IOS_REMEMBERED_EXTRA].includes(scheme) ? scheme : null;
};

/** Адрес страницы перебора; `skip` - схема, которую уже попробовали на странице оплаты. */
export const buildAlfaOpenPageUrl = (backPath: string, skip?: string | null): string => {
  const params = new URLSearchParams({ back: backPath });

  if (skip) {
    params.set('skip', skip);
  }

  return `${ALFA_OPEN_PAGE}?${params.toString()}`;
};

export type AlfaOpenResult =
  | { result: 'ok'; scheme: string; attempt: number }
  | { result: 'fail' }
  | { result: 'cancel' };

/** Итог перебора из адреса страницы оплаты (`?alfa_open=ok&scheme=…&attempt=…`). */
export const readAlfaOpenResult = (search: string): AlfaOpenResult | null => {
  const params = new URLSearchParams(search);
  const result = params.get(ALFA_OPEN_RESULT_PARAM);

  if (result === 'ok') {
    const scheme = params.get('scheme') ?? '';

    return {
      result,
      scheme: ALFA_QR_IOS_SCHEMES.includes(scheme) ? scheme : '',
      attempt: Number(params.get('attempt')) || 0,
    };
  }

  if (result === 'fail' || result === 'cancel') {
    return { result };
  }

  return null;
};

/** Адрес страницы оплаты без параметров итога перебора (остальные параметры не трогаем). */
export const stripAlfaOpenResult = (search: string): string => {
  const params = new URLSearchParams(search);

  [ALFA_OPEN_RESULT_PARAM, 'scheme', 'attempt'].forEach((key) => params.delete(key));

  const rest = params.toString();

  return rest ? `?${rest}` : '';
};
